import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { toast } from '@/clientWeb/components/ui/toast-utils'
import { useLanguage, useTranslation } from '@/clientWeb/i18n/LanguageContext'
import { selectIsAuthenticated, useAuthStore } from '@/features/auth/store/auth.store'
import { useDevicesStore } from '@/features/devices'
import {
  isMeetingError,
  isValidationError,
} from '../services/meetings-error'
import { meetingsService } from '../services/meetings.service'
import { meetingsApi, guestApi, ApiError } from '../services/meetings.api'
import { decideJoinFailure, describeSeriesRefusal } from '../services/join-failure'
import { rememberGuestPassword, guestPasswordFor } from '../services/guest-password'
import { noteMeetingFull, clearMeetingFull } from '../services/meeting-full-notice'
import { useMeetingSessionStore } from '@/features/meeting/store/meeting-session.store'
import { MEETING_ERROR_CODES } from '../constants/meetings.constants'
import type { MeetingRecord, MeetingSettings } from '../models/meeting.model'
import MeetingPasswordDialog from '../components/MeetingPasswordDialog'
import { useWebinarStore } from '@/features/webinar/store/webinar.store'
import { exitMeetingRoute } from '@/features/embed/meeting-exit'

// 10-digit numeric IDs belong to the local mock service.
// UUIDs and other formats come from the backend — skip mock validation.
const isLocalMockId = (id: string) => /^\d{10}$/.test(id)

const IS_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

type GuardStatus = 'splashing' | 'checking' | 'ok' | 'password_required' | 'blocked'

const SPLASH_DURATION_MS = 2000
/** How often an open password prompt asks whether the meeting still has a password. */
const PASSWORD_RECHECK_MS = 5000

/**
 * Pushes the meeting's media defaults into the devices store **synchronously**
 * before VcApp mounts, so the prejoin preview reflects them on first paint.
 *   muteOnEntry  → micEnabled = false
 *   videoOnEntry → camEnabled = true; otherwise camEnabled = false
 */
function applyMediaSettings(settings: MeetingSettings | undefined) {
  if (!settings) return
  const devices = useDevicesStore.getState()
  devices.setMicEnabled(!settings.muteOnEntry)
  devices.setCamEnabled(!!settings.videoOnEntry)
}

function MeetingSplash() {
  const t = useTranslation()
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-card relative overflow-hidden flex items-center justify-center px-4">
      <div className="relative z-10 w-full">
        <div className="p-6 sm:p-8 md:p-12 lg:p-16">
          <div className="flex justify-center mb-3 sm:mb-4">
            <div className="relative w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24">
              <div className="absolute inset-0 bg-primary/20 rounded-2xl blur-xl animate-pulse" />
              <img src="/InmeetLogo.svg" alt="logo" className="w-full h-full object-contain" />
            </div>
          </div>
          <h1 className="text-center">
            <span className="text-2xl dark:text-foreground sm:text-3xl md:text-2xl font-bold">InMeet</span>
          </h1>
          <p className="mt-3 sm:mt-4 dark:text-foreground/60 text-center text-base sm:text-lg md:text-xl text-slate-600 dark:text-muted-foreground font-medium">
            {t('vcApp.nextGenVideoConferencing')}
          </p>
          <p className="mt-2 text-center dark:text-foreground/60 text-sm sm:text-base md:text-lg text-slate-500 dark:text-muted-foreground">
            {t('vcApp.settingUpEverything')}
          </p>
          <div className="mt-4 sm:mt-6 flex justify-center">
            <div className="mb-4 sm:mb-6 flex items-center gap-1.5 sm:gap-2">
              <span className="h-3 w-3 sm:h-4 sm:w-4 rounded-full bg-[#0C5A96]/70 dark:bg-secondary animate-[dotPulse_0.9s_ease-in-out_infinite]" />
              <span className="h-3 w-3 sm:h-4 sm:w-4 rounded-full bg-[#0C5A96]/70 dark:bg-secondary animate-[dotPulse_0.9s_ease-in-out_infinite_0.15s]" />
              <span className="h-3 w-3 sm:h-4 sm:w-4 rounded-full bg-[#0C5A96]/70 dark:bg-secondary animate-[dotPulse_0.9s_ease-in-out_infinite_0.3s]" />
              <span className="h-3 w-3 sm:h-4 sm:w-4 rounded-full bg-[#0C5A96]/70 dark:bg-secondary animate-[dotPulse_0.9s_ease-in-out_infinite_0.3s]" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

interface MeetingGuardProps {
  children: ReactNode
}

/**
 * Wraps `/meeting/:meetingId` and only renders `children` once the meeting is
 * confirmed to exist, not expired/cancelled, joinable now, and (if required)
 * unlocked with the correct password.
 *
 * Anything else → toast + redirect to `/meetings`.
 */
export default function MeetingGuard({ children }: MeetingGuardProps) {
  const { t, language } = useLanguage()
  const navigate = useNavigate()
  const location = useLocation()
  const { meetingId = '' } = useParams<{ meetingId: string }>()
  const [searchParams] = useSearchParams()
  const authUser = useAuthStore((s) => s.user)
  // The session predicate, not `user` — `user` is hydrated from localStorage and
  // outlives the session, so it reports "signed in" for a browser that only ever
  // had someone signed in once. ProtectedRoute and VcApp both gate on this same
  // selector; the guard has to agree with them or it blocks people they admit.
  const isAuthenticated = useAuthStore(selectIsAuthenticated)
  const webinars = useWebinarStore((s) => s.webinars)

  const [status, setStatus] = useState<GuardStatus>('splashing')
  const [splashDone, setSplashDone] = useState(false)
  const [meeting, setMeeting] = useState<MeetingRecord | null>(null)
  const [passwordError, setPasswordError] = useState<string | undefined>()

  /**
   * The password was accepted — so a close is success, not a cancel.
   *
   * `MeetingPasswordDialog` calls `onOpenChange(false)` when it succeeds, the
   * same call it makes when the user dismisses it. The handler below could not
   * tell them apart and navigated to /meetings either way — which for a
   * signed-out visitor is the sign-in page. Typing the CORRECT password
   * therefore threw you out, and `status` was no help: `setStatus('ok')` has
   * not re-rendered yet, so the closure still reads 'password_required'.
   *
   * A ref, not state, because it is read inside that close handler in the same
   * tick the submit resolves.
   */
  const unlockedRef = useRef(false)

  // Always show the InMeet splash for SPLASH_DURATION_MS before doing any
  // evaluation. Once the splash finishes we run the existence / password
  // check; the user only sees the password dialog *after* the brand splash.
  useEffect(() => {
    const t = window.setTimeout(() => setSplashDone(true), SPLASH_DURATION_MS)
    return () => window.clearTimeout(t)
  }, [])

  const redirectAway = useCallback(
    (message: string) => {
      toast.error({ title: t('meetings.cannotJoinMeeting'), description: message })
      exitMeetingRoute(navigate, { reason: 'cannot-join', message, replace: true })
    },
    [navigate, t],
  )

  // Deleting a meeting soft-deletes it to `cancelled`, and both join and the
  // guest preview answer 410 MEETING_CANCELLED for it. Treating that like any
  // other failure told an invitee "Cannot join meeting — we couldn't find a
  // meeting with that ID", which reads as a bad link, not a deleted meeting.
  const redirectIfDeleted = useCallback(
    (err: unknown): boolean => {
      if (!(err instanceof ApiError) || err.code !== MEETING_ERROR_CODES.MEETING_CANCELLED) {
        return false
      }
      toast.error({ title: t('meetings.meetingDeleted'), description: t('meetings.errors.MEETING_CANCELLED') })
      exitMeetingRoute(navigate, {
        reason: 'meeting-cancelled',
        message: t('meetings.errors.MEETING_CANCELLED'),
        replace: true,
      })
      return true
    },
    [navigate, t],
  )

  // The code the URL was just swapped to. The swap changes `meetingId`, which
  // re-runs `evaluate`; this lets that run through without joining a second
  // time — a second join would queue a second lobby request.
  const canonicalIdRef = useRef<string | null>(null)

  /**
   * Swap a `/meeting/<uuid>` address for `/meeting/<code>`, once the server
   * has confirmed the meeting and told us its code.
   *
   * Links carrying the uuid are still out there (shared before the dashboard
   * switched to the code), and both open the same meeting — the backend
   * resolves either. This only changes what the address bar shows, and so what
   * gets copied out of it.
   *
   * Returns true when it navigated. The caller must then NOT set 'ok' itself:
   * the router applies the URL change as a transition, so an urgent 'ok' here
   * would mount the room under the old URL first, and the key change on the
   * wrapper below would then tear it down and mount it again — prejoin and
   * camera twice. The re-run on the new URL sets 'ok' instead.
   */
  const showCodeInUrl = useCallback(
    (code: unknown): boolean => {
      if (typeof code !== 'string' || !code || code === meetingId) return false
      if (!IS_UUID.test(meetingId)) return false
      canonicalIdRef.current = code
      navigate(
        { pathname: `/meeting/${encodeURIComponent(code)}`, search: window.location.search },
        { replace: true, state: location.state },
      )
      return true
    },
    [location.state, meetingId, navigate],
  )

  const evaluate = useCallback(async () => {
    if (!splashDone) return
    if (canonicalIdRef.current === meetingId) {
      canonicalIdRef.current = null
      setStatus('ok')
      return
    }
    setStatus('checking')
    setPasswordError(undefined)
    // Said by this check or not at all: a "full" from an earlier one is stale.
    clearMeetingFull()

    // Guest join page (GuestJoinPage /invite/:token) sets guestMode on the
    // navigation state after the backend already admitted the user and stored
    // credentials. Skip all validation — we're good to go.
    if ((location.state as any)?.guestMode) {
      setStatus('ok')
      return
    }

    // No session → guest, exactly as ProtectedRoute(allowGuest) and VcApp decide
    // it. `?role=guest` stays honoured for links handed out before that changed,
    // but it is no longer what makes someone a guest: the link copied out of the
    // meeting info panel carries no flag, and requiring one meant a signed-out
    // visitor fell through to the authed `meetingsApi.join` below, took a 401,
    // and got bounced to /meetings — i.e. to /login. Only the ?role=guest copy
    // of the very same link worked.
    //
    // A guest has no pre-seeded credentials; they are validated and admitted on
    // the "Join" click inside VcApp (guestApi.guestJoinOpen, waiting room
    // included). Here we only confirm the meeting exists via the public preview,
    // so a bad link still doesn't mount the room.
    if (!isAuthenticated) {
      try {
        const { meeting: preview } = await guestApi.getMeetingPreview(meetingId)
        // Same door a signed-in user meets, at the same point.
        //
        // The preview has always reported `passwordRequired` — its own comment
        // says it exists "so the client knows to ask BEFORE attempting to
        // join" — and this branch ignored it, so a guest was walked through
        // the camera preview and only challenged on the Join click. Two
        // different orders for the same protected meeting, depending on
        // whether you happened to be signed in.
        //
        // The join itself still happens later, on Join, because it needs a
        // name that this screen has not collected yet. What is verified here
        // is only the password; `rememberGuestPassword` carries it across.
        // Gated on the PASSWORD, not on the access grant.
        //
        // A guest join needs the password itself — `guestJoinOpen` sends it —
        // so a grant without one is useless here. Keying off the grant meant a
        // guest who had unlocked this meeting in an earlier visit (or whose
        // password was cleared on leave) walked straight past the challenge,
        // and VcApp had to ask on the Join click instead: preview first, then
        // the password, which is the order this branch exists to remove.
        if (preview?.passwordRequired && !guestPasswordFor(meetingId)) {
          setStatus('password_required')
          return
        }
        // No URL swap for a protected meeting: the verified password is kept
        // under the id in the URL (`rememberGuestPassword(meetingId)`), and
        // VcApp looks it up by the URL id — swapping to the code would lose it
        // and ask the guest a second time on the Join click.
        if (preview?.passwordRequired || !showCodeInUrl(preview?.meetingId)) setStatus('ok')
      } catch (err) {
        // A series link on a day it cannot open says which day, before the
        // guest has typed a name.
        const outcome = decideJoinFailure(err, false)
        if (outcome.kind === 'series') redirectAway(describeSeriesRefusal(outcome, t, language))
        // The key `decideJoinFailure` chose, as the signed-in branch below
        // uses it. A fixed "not found" told a guest whose meeting had been
        // marked past that the link was wrong, rather than that it was over.
        else if (!redirectIfDeleted(err)) {
          const key = outcome.kind === 'redirect' ? outcome.messageKey : MEETING_ERROR_CODES.MEETING_NOT_FOUND
          redirectAway(t(`meetings.errors.${key}`))
        }
        setStatus('blocked')
      }
      return
    }

    const userName = searchParams.get('name') || authUser?.name || 'Guest'

    // Backend-created meetings (UUIDs, non-10-digit IDs) bypass the local
    // mock validator entirely and are validated via the real API instead.
    if (!isLocalMockId(meetingId)) {
      // Already answered, this tab, this meeting.
      //
      // `evaluate` re-runs whenever its inputs change — `searchParams` shifts
      // the moment VcApp strips `?name=` from the URL — and the pre-fetch below
      // deliberately sends no password, so every re-run 401s and re-opens the
      // dialog on somebody who has already cleared it. Dismissing that second
      // dialog navigates to /meetings, which for a signed-out visitor is the
      // sign-in page: password accepted, and then thrown out.
      //
      // The grant is sessionStorage with an expiry, the same one the local-mock
      // branch below has always used. Credentials are required too: the grant
      // can outlive the store it was taken with, and admitting someone with no
      // credentials only moves the failure into VcApp.
      //
      // Asked of the server all the same, and honoured only for the password
      // (see the catch below). Answering 'ok' here without asking let a
      // participant back into a meeting the host had since marked as past:
      // the stored credentials were reused and the server never heard of it.
      const clearedThisTab = () =>
        meetingsService.hasGrantedAccess(meetingId) &&
        !!useMeetingSessionStore.getState().roomCredentials

      try {
        // Only asks whether this person could join. This used to be the real
        // join, to have credentials ready before the Join click, but a real
        // join on a locked room files a lobby request: the host was asked to
        // admit someone still on the preview, the admit reached nobody, and
        // the Join click then asked a second time. `check` stops the server
        // before the lobby and issues nothing; the Join click in VcApp does
        // the join, admitted or waiting. A password already verified in this
        // tab rides along so a re-run does not ask again.
        const data = await meetingsApi.join(meetingId, {
          check: true,
          password: guestPasswordFor(meetingId) ?? undefined,
        })
        // A server that predates `check` joins instead; keep what it issued.
        if (data.roomCredentials) {
          useMeetingSessionStore.getState().setRoomCredentials(data.roomCredentials)
        }
        if (!showCodeInUrl(data.meeting?.meetingId)) setStatus('ok')
      } catch (err) {
        // This pre-fetch deliberately sends no password, so a protected meeting
        // answers 401 INVALID_PASSWORD every time. The catch used to discard
        // the code and report MEETING_NOT_FOUND for anything that threw, so an
        // invited participant was bounced to /meetings and told the meeting did
        // not exist — while the host walked straight in, because the server
        // exempts them from the password check. `handlePasswordSubmit` already
        // replays the join with a password for backend meetings; all this
        // branch had to do was ask. The rule lives in `decideJoinFailure` so it
        // can be tested without a DOM.
        const outcome = decideJoinFailure(err, webinars.some((w) => w.id === meetingId))
        // The server checks "ended" before the password, so reaching here
        // means the meeting is still open and only the password is missing.
        if (outcome.kind === 'password') {
          setStatus(clearedThisTab() ? 'ok' : 'password_required')
          return
        }
        if (outcome.kind === 'webinar') { setStatus('ok'); return }
        // No seat left right now. On to the preview all the same: VcApp opens
        // the "meeting is full" dialog there, with Try again, rather than this
        // sending them back to the dashboard.
        if (outcome.kind === 'full') {
          noteMeetingFull(meetingId)
          setStatus('ok')
          return
        }
        if (outcome.kind === 'series') {
          redirectAway(describeSeriesRefusal(outcome, t, language))
          setStatus('blocked')
          return
        }
        if (!redirectIfDeleted(err)) redirectAway(t(`meetings.errors.${outcome.messageKey}`))
        setStatus('blocked')
      }
      return
    }

    // Local mock path — 10-digit numeric IDs from the old local-only flow.
    try {
      const provisional = await meetingsService.validateJoin({
        meetingId,
        name: userName,
      })
      setMeeting(provisional)
      applyMediaSettings(provisional.settings)
      setStatus('ok')
    } catch (err) {
      if (isValidationError(err)) {
        const first = Object.values(err.errors)[0] ?? t('meetings.errors.UNKNOWN')
        redirectAway(first)
        setStatus('blocked')
        return
      }
      if (isMeetingError(err)) {
        if (err.code === MEETING_ERROR_CODES.PASSWORD_REQUIRED) {
          const record = meetingsService.getById(meetingId)
          setMeeting(record ?? null)
          if (record && meetingsService.hasGrantedAccess(meetingId)) {
            setStatus('ok')
            return
          }
          setStatus('password_required')
          return
        }
        if (err.code === MEETING_ERROR_CODES.MEETING_NOT_FOUND) {
          const webinar = webinars.find((w) => w.id === meetingId)
          if (webinar) {
            setStatus('ok')
            return
          }
        }
        redirectAway(t(`meetings.errors.${err.code}`))
        setStatus('blocked')
        return
      }
      redirectAway(t('meetings.errors.UNKNOWN'))
      setStatus('blocked')
    }
  }, [authUser?.email, authUser?.id, authUser?.name, isAuthenticated, language, location.state, meetingId, redirectAway, redirectIfDeleted, searchParams, showCodeInUrl, splashDone, t, webinars])

  // Re-run whenever the URL meetingId changes (and once the splash is done).
  useEffect(() => {
    void evaluate()
  }, [evaluate])

  // While the password prompt is up, keep asking whether it is still needed.
  //
  // The prompt was decided once, when the link opened. If the host then took
  // the password off, this page kept asking for it — and since the server no
  // longer checks one, whatever was typed (the old password included) was
  // "accepted". Now the page notices within a few seconds and re-runs the
  // normal flow: no password, straight on to the preview. A meeting that was
  // ended or cancelled meanwhile is caught the same way and refused as usual.
  useEffect(() => {
    if (status !== 'password_required') return
    if (isAuthenticated && isLocalMockId(meetingId)) return
    let busy = false
    const timer = window.setInterval(async () => {
      if (busy) return
      busy = true
      try {
        const stillRequired = isAuthenticated
          ? await meetingsApi.join(meetingId, { check: true }).then(
              () => false,
              // Only a wrong (here: missing) password means "still protected".
              // A network failure says nothing either way, so keep asking.
              (err) => !(err instanceof ApiError) || err.code === MEETING_ERROR_CODES.INVALID_PASSWORD,
            )
          : await guestApi.getMeetingPreview(meetingId).then(
              ({ meeting: preview }) => !!preview?.passwordRequired,
              () => true,
            )
        if (!stillRequired) void evaluate()
      } finally {
        busy = false
      }
    }, PASSWORD_RECHECK_MS)
    return () => window.clearInterval(timer)
  }, [status, isAuthenticated, meetingId, evaluate])

  const handlePasswordSubmit = useCallback(
    async (password: string): Promise<true | { ok: false; message: string }> => {
      try {
        // A guest has no session to join with, and no name yet — the name is
        // collected on the preview this dialog stands in front of. So the
        // password is verified on its own and carried to the Join click, which
        // is where the guest join actually happens.
        if (!isAuthenticated) {
          await guestApi.verifyMeetingPassword(meetingId, password)
          rememberGuestPassword(meetingId, password)
          meetingsService.grantAccess(meetingId)
          unlockedRef.current = true
          setStatus('ok')
          return true
        }

        // Backend meetings verify server-side; the local mock keeps its own
        // path. Checking here rather than in the dialog keeps the dialog a
        // dumb form that reports whatever it is told.
        if (!isLocalMockId(meetingId)) {
          // Verified with `check`, like the first look above, so a locked room
          // gets no lobby request from the password dialog either. The Join
          // click sends the password again, so it is kept under the address
          // VcApp will read it from: the code, once `showCodeInUrl` moves there.
          const data = await meetingsApi.join(meetingId, { password, check: true })
          if (data.roomCredentials) {
            useMeetingSessionStore.getState().setRoomCredentials(data.roomCredentials)
          }
          const verifiedCode = data.meeting?.meetingId
          rememberGuestPassword(
            typeof verifiedCode === 'string' && verifiedCode ? verifiedCode : meetingId,
            password,
          )
          applyMediaSettings((data.meeting?.settings ?? undefined) as MeetingSettings | undefined)
          // So a re-run of `evaluate` does not ask again — see the grant check
          // in the backend branch above. Granted under the code as well, since
          // `showCodeInUrl` may be about to move the URL onto it.
          meetingsService.grantAccess(meetingId)
          const code = data.meeting?.meetingId
          if (typeof code === 'string' && code) meetingsService.grantAccess(code)
          unlockedRef.current = true
          if (!showCodeInUrl(code)) setStatus('ok')
          return true
        }

        const userName = searchParams.get('name') || authUser?.name || 'Guest'
        await meetingsService.validateJoin({
          meetingId,
          name: userName,
          password,
        })
        meetingsService.grantAccess(meetingId)
        unlockedRef.current = true
        applyMediaSettings(meeting?.settings)
        setStatus('ok')
        return true
      } catch (err) {
        // The server distinguishes a wrong password from everything else, and
        // the joiner needs to be told which — retyping helps for one and not
        // the other.
        if (err instanceof ApiError) {
          // Right password (the server checks it before the seats), but no
          // seat left. The password is accepted and kept for the Join click,
          // and the preview opens with the "meeting is full" dialog on it.
          if (err.code === MEETING_ERROR_CODES.MEETING_FULL) {
            rememberGuestPassword(meetingId, password)
            meetingsService.grantAccess(meetingId)
            noteMeetingFull(meetingId)
            unlockedRef.current = true
            setStatus('ok')
            return true
          }
          return {
            ok: false,
            message:
              err.code === MEETING_ERROR_CODES.INVALID_PASSWORD
                ? t('meetings.errors.INVALID_PASSWORD')
                : err.message || t('meetings.errors.UNKNOWN'),
          }
        }
        if (isMeetingError(err)) {
          return { ok: false, message: t(`meetings.errors.${err.code}`) }
        }
        return { ok: false, message: t('meetings.errors.UNKNOWN') }
      }
    },
    [authUser?.name, isAuthenticated, meeting?.settings, meetingId, searchParams, showCodeInUrl, t],
  )

  if (status === 'splashing' || status === 'checking' || status === 'blocked') {
    return <MeetingSplash />
  }

  if (status === 'password_required') {
    return (
      <>
        <MeetingSplash />
        <MeetingPasswordDialog
          open
          onOpenChange={(open) => {
            if (open) return
            // Closed because it was accepted — the guard is already moving to
            // the preview. Only a dismissal is a reason to leave.
            if (unlockedRef.current) return
            exitMeetingRoute(navigate, { reason: 'password-cancelled', replace: true })
          }}
          meetingTitle={meeting?.title}
          initialError={passwordError}
          onSubmit={handlePasswordSubmit}
          onCancel={() => exitMeetingRoute(navigate, { reason: 'password-cancelled', replace: true })}
        />
      </>
    )
  }

  // We block rerenders triggered purely by location changes from the children.
  // The key guarantees a clean mount when the route id actually changes.
  return <div key={`meeting-${meetingId}-${location.key}`}>{children}</div>
}
